import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Drawer, Loader } from '@mantine/core';
import type { Stripe } from '@stripe/stripe-js';
import { useTranslation, Trans } from 'react-i18next';
import { cartActions } from '../../reducers/storeCart';
import { billingAccountQuery } from '../../graphql-operations/BillingAccount.graphql';
import { updateShippingAddressMutation } from '../../graphql-operations/ShippingInfo.graphql';
import { priceCartForCheckout } from '../../lib/pricing';
import { executeStoreShopMutation } from '../../lib/purchase';
import { useLocale } from '../../contexts/LocaleContext';
import { centsToDollarsNumber, getLocalizedPricing } from '../../lib/util';
import { inAppShop } from '../../lib/analytics/events';
import { logInternalError } from '../../lib/errors';
import useHardwareFirstMembershipTerms from '../../hooks/useHardwareFirstMembershipTerms';
import useProductSkuMap from '../../hooks/useProductSkuMap';
import { isBundleCartItem } from '../../lib/cart';
import { lowestMonthlyPrice } from '../../lib/hardwareFirst';
import AppPaths from '../../AppPaths';
import * as types from '../../types';
import { gqlTypes } from '../../types';
import { FREE_SHIPPING_CODE } from '../../types/Product';
import { isEnrolledInPositionDiscount } from '../../lib/positionDiscount';
import { ReactComponent as TrashIcon } from '../../assets/images/icons/trash.svg';
import { checkoutImageUrlForProduct, checkoutImageUrlForProductWithVariant } from '../CartItemList/CartItemList';
import HardwareFirstDisclosure from '../HardwareFirstDisclosure/HardwareFirstDisclosure';
import {
  accountEditErrorMessage,
  billingDetailsPrefillFromAccount,
  saveStripePaymentMethod,
} from './inAppSheetPaymentMethod';
import InAppBillingPanel from './InAppBillingPanel';
import InAppShippingPanel from './InAppShippingPanel';
import { shippingAddressVariables } from './inAppSheetAccountEdit';
import type { StripeAddressValue } from '../StripeCheckout';
import inAppSheetLinePrice from './inAppSheetLinePrice';
import styles from './InAppCheckoutSheet.module.scss';

export default function InAppCheckoutSheet() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { country, currency, currencyFormatter } = useLocale();
  const productSkuMap = useProductSkuMap();
  const lowestMonthly = lowestMonthlyPrice(useHardwareFirstMembershipTerms());

  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const session = useSelector((state: types.AppState) => state.session);
  const opened = cart.showInAppCheckout === true;
  const cartItems = useMemo(() => Object.values(cart.cartItems), [cart.cartItems]);

  const [pricing, setPricing] = useState<types.CartPricing | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<'shipping' | 'billing' | null>(null);
  const [savingAccount, setSavingAccount] = useState(false);

  const {
    data: accountData,
    loading: accountLoading,
    refetch: refetchAccount,
  } = useQuery<gqlTypes.billingAccount>(billingAccountQuery, {
    skip: !opened || !session,
    fetchPolicy: 'network-only',
  });
  const [updateShippingAddress] = useMutation<gqlTypes.updateShippingAddress, gqlTypes.updateShippingAddressVariables>(
    updateShippingAddressMutation,
  );

  const billingAccount = accountData?.currentUser?.billingAccount;
  const shippingAddress = billingAccount?.address;
  const paymentInfo = billingAccount?.billingInfo?.paymentInfo;
  const hasShipping = !!shippingAddress;
  const hasBilling = !!paymentInfo;
  // false when another account is billed for the membership, so a card stored here would never be charged
  const canEditBilling = billingAccount?.canUpdatePaymentInfo !== false;

  useEffect(() => {
    if (!opened || cartItems.length === 0) return;

    setPricingLoading(true);
    setError(null);

    const address = shippingAddress ? { country: shippingAddress.country, postalCode: shippingAddress.zip } : undefined;

    // a code auto-applied while a request is in flight re-runs this effect; the superseded response
    // must not land after the current one and price the cart without the code
    let superseded = false;

    // in-app purchases always qualify for free shipping — users are existing subscribers buying accessories
    priceCartForCheckout(cart, {
      checkoutType: types.CheckoutType.Store,
      shippingCode: FREE_SHIPPING_CODE,
      address,
    })
      .then((result) => {
        if (!superseded) setPricing(result);
      })
      .catch(() => {
        if (!superseded) setError(t('order_failed'));
      })
      .finally(() => {
        if (!superseded) setPricingLoading(false);
      });

    return () => {
      superseded = true;
    };
  }, [opened, cartItems.length, shippingAddress, cart, t]);

  useEffect(() => {
    if (opened && cartItems.length === 0) {
      dispatch(cartActions.setShowInAppCheckout(false));
    }
  }, [opened, cartItems.length, dispatch]);

  useEffect(() => {
    if (!opened) setEditMode(null);
  }, [opened]);

  // fire analytics on open
  useEffect(() => {
    if (opened) {
      try {
        inAppShop.checkoutOpened();
      } catch (err) {
        logInternalError(err);
      }
    }
  }, [opened]);

  const close = useCallback(() => {
    dispatch(cartActions.setShowInAppCheckout(false));
  }, [dispatch]);

  const removeItem = useCallback(
    (id: string) => {
      inAppShop.itemRemoved();
      dispatch(cartActions.removeCartItem({ id }));
    },
    [dispatch],
  );

  const confirmOrder = useCallback(async () => {
    if (!hasShipping || !hasBilling) return;

    setPurchasing(true);
    setError(null);
    inAppShop.orderConfirmed();

    try {
      // in-app users are pre-authenticated with billing on file — the backend
      // uses the stored payment method so we only need to pass shippingCode.
      // in-app purchases always qualify for free shipping (existing subscribers buying accessories).
      // 3DS is not supported in this flow; if required, the catch block
      // directs the user to the full checkout.
      await executeStoreShopMutation(cart, {
        shippingCode: FREE_SHIPPING_CODE,
      });
      inAppShop.orderCompleted();
      dispatch(cartActions.resetCart({ resetCoupon: true }));
      dispatch(cartActions.setShowInAppCheckout(false));
      history.push(AppPaths.CheckoutThankYou);
    } catch (err) {
      logInternalError(err, {
        fingerprint: ['in-app-checkout-order-failed'],
        tags: { context: 'inAppCheckoutConfirmOrder' },
      });
      inAppShop.orderFailed();
      setError(t('order_failed'));
    } finally {
      setPurchasing(false);
    }
  }, [cart, hasShipping, hasBilling, dispatch, history, t]);

  const goToFullCheckout = useCallback(() => {
    inAppShop.fullCheckoutTapped();
    close();
    history.push(AppPaths.CheckoutStripe);
  }, [close, history]);

  // the panels save to the member's account, so without a session the full checkout signs them up first
  const openAccountEdit = useCallback(
    (field: 'shipping' | 'billing') => {
      if (!session) {
        goToFullCheckout();
        return;
      }
      inAppShop.accountEditOpened(field);
      setEditMode(field);
    },
    [session, goToFullCheckout],
  );

  // the account keeps the address and card, and the purchase reads both from there, so a save here
  // is all the order needs
  const saveAccountEdit = useCallback(
    async (field: 'shipping' | 'billing', save: () => Promise<unknown>) => {
      setSavingAccount(true);
      setError(null);
      try {
        await save();
        await refetchAccount();
        inAppShop.accountEditSaved(field);
      } catch (err) {
        logInternalError(err, { tags: { context: 'inAppCheckoutAccountEdit', field } });
        setError(accountEditErrorMessage(err, t));
      } finally {
        setEditMode(null);
        setSavingAccount(false);
      }
    },
    [refetchAccount, t],
  );

  const handleUpdateAddress = useCallback(
    (value: StripeAddressValue) =>
      saveAccountEdit('shipping', async () => {
        const result = await updateShippingAddress({ variables: shippingAddressVariables(value) });
        if (!result.data?.updateBillingAccount?.address) {
          throw new Error('updateShippingAddress resolved without an updated address');
        }
      }),
    [saveAccountEdit, updateShippingAddress],
  );

  const handleUpdateBilling = useCallback(
    (confirmationTokenId: string, stripe: Stripe) =>
      saveAccountEdit('billing', () => saveStripePaymentMethod(stripe, confirmationTokenId)),
    [saveAccountEdit],
  );

  const formatCents = useCallback(
    (cents: number) => currencyFormatter.format(centsToDollarsNumber(cents)),
    [currencyFormatter],
  );

  const loading = accountLoading || pricingLoading;
  // the server prices standalone collars as hardware-first units; the sheet follows its figures
  const hardwareDiscounts = pricing?.hardwareDiscounts;
  const hasHardwareFirstItems = !!hardwareDiscounts && Object.keys(hardwareDiscounts).length > 0;
  // the server subtotal already nets both hardware savings out of the unit prices; the summary
  // restates the list subtotal and shows each saving as its own row so the column adds up
  const hardwarePromoInCents = Object.values(hardwareDiscounts ?? {}).reduce(
    (sum, discount) => sum + discount.promoDiscountInCents,
    0,
  );
  const hardwareListInCents = Object.values(hardwareDiscounts ?? {}).reduce(
    (sum, discount) => sum + discount.listPriceInCents,
    0,
  );
  const promoPercentOff = hardwareListInCents > 0 ? Math.round((hardwarePromoInCents / hardwareListInCents) * 100) : 0;
  const multiDeviceInCents = pricing?.hardwareFirstMultiDeviceDiscountInCents ?? 0;
  const listSubtotalInCents = (pricing?.subtotalInCents ?? 0) + hardwarePromoInCents + multiDeviceInCents;
  const promoApplied = !!cart.couponCode || (!!pricing && pricing.appliedDiscountInCents > 0);
  const multiPetPercentOff = Math.max(
    0,
    ...Object.values(pricing?.positionDiscounts ?? {})
      .filter((discount) => discount.isEnrolled)
      .map((discount) => discount.discountPercent),
  );
  const appliedPercentOff = cart.couponCode ? promoPercentOff : multiPetPercentOff;

  const paymentLabel = useMemo(() => {
    if (!paymentInfo) return null;
    if (paymentInfo.__typename === 'ObfuscatedCardInfo') {
      return {
        primary: `${t('card_ending_in')} ${paymentInfo.lastFour}`,
        secondary: `${t('expires')} ${paymentInfo.month}/${paymentInfo.year}`,
      };
    }
    if (paymentInfo.__typename === 'BankAccountInfo') {
      return {
        primary: `${paymentInfo.bankName} ····${paymentInfo.last4}`,
        secondary: paymentInfo.accountType,
      };
    }
    if (paymentInfo.__typename === 'WalletPaymentInfo') {
      return { primary: paymentInfo.type, secondary: t('pay_with_digital_wallet') };
    }
    return null;
  }, [paymentInfo, t]);

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position="bottom"
      size="auto"
      withCloseButton
      closeOnEscape={!savingAccount}
      closeOnClickOutside={!savingAccount}
      title={t('in_app_checkout_title')}
      closeButtonProps={{ iconSize: 24, size: 30, color: 'dark' }}
      styles={() => ({
        inner: {
          '.mantine-Drawer-content': {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column' as const,
          },
        },
        title: {
          fontWeight: 700,
          fontSize: 20,
          width: '100%',
          textAlign: 'center' as const,
        },
        header: {
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)',
          justifyContent: 'center',
          flexShrink: 0,
        },
        body: {
          flex: 1,
          overflow: 'auto',
        },
      })}
    >
      <div className={styles.body}>
        {editMode === 'shipping' && !savingAccount ? (
          <InAppShippingPanel
            account={billingAccount}
            onSubmit={(value) => void handleUpdateAddress(value)}
            onCancel={() => setEditMode(null)}
          />
        ) : editMode === 'billing' && !savingAccount ? (
          <InAppBillingPanel
            hasShipping={hasShipping}
            hasBilling={hasBilling}
            billingDetailsPrefill={billingDetailsPrefillFromAccount(billingAccount, country)}
            onSubmit={(tokenId, stripe) => void handleUpdateBilling(tokenId, stripe)}
            onCancel={() => setEditMode(null)}
            saving={savingAccount}
          />
        ) : loading || savingAccount ? (
          <div className={styles.loading}>
            <Loader color="dark" />
          </div>
        ) : (
          <>
            {/* cart items */}
            {cartItems.map((item) => {
              const { product, variant } = productSkuMap.get(item.lineItem.sku) ?? {};
              if (!product) return null;

              const subProduct = isBundleCartItem(item)
                ? productSkuMap.get(item.subscriptionLineItem.sku)?.product
                : undefined;
              const hardwareDiscount = subProduct ? undefined : hardwareDiscounts?.[item.cartItemId];
              const subtitle = hardwareDiscount
                ? lowestMonthly && t('hardware_first_cart_plan_required', { monthly: lowestMonthly })
                : (subProduct?.name ?? '');
              const { priceInCents, listPriceInCents } = inAppSheetLinePrice({
                productPriceInCents: getLocalizedPricing(product.pricingInCents, currency),
                subProductPriceInCents: subProduct
                  ? getLocalizedPricing(subProduct.pricingInCents, currency)
                  : undefined,
                hardwareDiscount,
              });
              const imageUrl = variant
                ? checkoutImageUrlForProductWithVariant(product.id, variant)
                : checkoutImageUrlForProduct(product.id);

              return (
                <div key={item.cartItemId} className={styles.cartItem}>
                  <img src={imageUrl} alt={product.name} className={styles.cartItemImage} />
                  <div className={styles.cartItemInfo}>
                    <div className={styles.cartItemTitle}>{product.name}</div>
                    {subtitle && <div className={styles.cartItemSubtitle}>{subtitle}</div>}
                  </div>
                  <div className={styles.cartItemRight}>
                    <div className={styles.cartItemPrice}>
                      {listPriceInCents !== undefined && (
                        <span className={styles.cartItemListPrice}>{formatCents(listPriceInCents)}</span>
                      )}
                      {formatCents(priceInCents)}
                    </div>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => removeItem(item.cartItemId)}
                      aria-label={t('remove')}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              );
            })}

            {hasHardwareFirstItems && (
              <div className={styles.section}>
                <HardwareFirstDisclosure hidePlanLine />
              </div>
            )}

            {/* shipping */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>{t('shipping')}</span>
                {hasShipping && (
                  <button type="button" className={styles.editLink} onClick={() => openAccountEdit('shipping')}>
                    {t('edit')}
                  </button>
                )}
              </div>
              {hasShipping ? (
                <div className={styles.sectionBody}>
                  {billingAccount?.firstName} {billingAccount?.lastName}
                  <br />
                  {shippingAddress.line1}
                  {shippingAddress.line2 && (
                    <>
                      <br />
                      {shippingAddress.line2}
                    </>
                  )}
                  <br />
                  {shippingAddress.city}
                  {', '}
                  {shippingAddress.state} {shippingAddress.zip}
                </div>
              ) : (
                <button type="button" className={styles.editLink} onClick={() => openAccountEdit('shipping')}>
                  {t('add_shipping_address')}
                </button>
              )}
            </div>

            {/* billing */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>{t('billing')}</span>
                {hasBilling && canEditBilling && (
                  <button type="button" className={styles.editLink} onClick={() => openAccountEdit('billing')}>
                    {t('edit')}
                  </button>
                )}
              </div>
              {paymentLabel ? (
                <div className={styles.sectionBody}>
                  {paymentLabel.primary}
                  <br />
                  {paymentLabel.secondary}
                </div>
              ) : canEditBilling ? (
                <button type="button" className={styles.editLink} onClick={() => openAccountEdit('billing')}>
                  {t('add_payment_method')}
                </button>
              ) : (
                <div className={styles.sectionBody}>{t('membership_billed_by_another_account')}</div>
              )}
            </div>

            {/* promo banner */}
            {promoApplied && (
              <div className={styles.promoSection}>
                <div className={styles.promoBanner}>
                  <span className={styles.promoLabel}>
                    {cart.couponCode ? (pricing?.couponDiscount?.name ?? cart.couponCode) : t('multi_pet_discount')}
                  </span>
                  <span className={styles.promoDescription}>
                    {appliedPercentOff > 0
                      ? t('percent_off_applied', { percent: appliedPercentOff })
                      : cart.couponCode
                        ? t('promo_code_applied')
                        : t('multi_pet_discount_description')}
                  </span>
                </div>
              </div>
            )}

            {/* order summary */}
            {pricing && (
              <div className={promoApplied ? styles.sectionNoBorder : styles.section}>
                <div className={styles.summaryRow}>
                  <span>{t('subtotal')}</span>
                  <span>{formatCents(listSubtotalInCents)}</span>
                </div>
                {hardwarePromoInCents > 0 && (
                  <div className={`${styles.summaryRow} ${styles.summaryDiscount}`}>
                    <span>{t('hardware_first_promo_code_discount')}</span>
                    <span>{`-${formatCents(hardwarePromoInCents)}`}</span>
                  </div>
                )}
                {multiDeviceInCents > 0 && (
                  <div className={`${styles.summaryRow} ${styles.summaryDiscount}`}>
                    <span>{t('hardware_first_cart_multi_device_discount')}</span>
                    <span>{`-${formatCents(multiDeviceInCents)}`}</span>
                  </div>
                )}
                {pricing.appliedDiscountInCents > 0 && (
                  <div className={`${styles.summaryRow} ${styles.summaryDiscount}`}>
                    <span>{isEnrolledInPositionDiscount(pricing) ? t('multi_pet_discount') : t('discount')}</span>
                    <span>{`-${formatCents(pricing.appliedDiscountInCents)}`}</span>
                  </div>
                )}
                <div className={styles.summaryRow}>
                  <span>{t('shipping')}</span>
                  <span>{pricing.shippingInCents ? formatCents(pricing.shippingInCents) : t('free')}</span>
                </div>
                {pricing.taxInCents != null && pricing.taxInCents > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('taxes')}</span>
                    <span>{formatCents(pricing.taxInCents)}</span>
                  </div>
                )}
                <div className={styles.totalRow}>
                  <span>{t('total')}</span>
                  <span>{formatCents(pricing.totalInCents)}</span>
                </div>
              </div>
            )}

            {/* error */}
            {error && (
              <>
                <div className={styles.error}>{error}</div>
                <button type="button" className={styles.fallbackLink} onClick={goToFullCheckout}>
                  {t('try_full_checkout')}
                </button>
              </>
            )}

            {/* actions */}
            {hasShipping && hasBilling ? (
              <button
                type="button"
                className={styles.confirmButton}
                onClick={() => void confirmOrder()}
                disabled={purchasing}
              >
                {purchasing ? <Loader color="white" size="sm" /> : t('confirm_order')}
              </button>
            ) : (
              <button type="button" className={styles.confirmButton} onClick={goToFullCheckout}>
                {t('go_to_checkout')}
              </button>
            )}
            <div className={styles.terms}>
              <Trans
                i18nKey="in_app_checkout_terms"
                components={{
                  termsLink: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a href="https://fitracking.com/tos" target="_blank" rel="noopener noreferrer" />
                  ),
                }}
              />
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
